import { createReducer, type Reducer } from "@reduxjs/toolkit";

import type {
  AddHandlerAction,
  HandlersByIdState,
  RemoveHandlerAction,
  UpdateHandlerAction,
} from "../../../schemas/index.ts";
import { addHandlerAction, removeHandlerAction, updateHandlerAction } from "./triggerActions.ts";
import type { TriggersReducerOptions } from "./triggersReducerOptions.ts";

export type HandlerEntryAction = AddHandlerAction | RemoveHandlerAction | UpdateHandlerAction;

interface Options {
  notifyEvent?: TriggersReducerOptions["notifyEvent"];
}

export const handlersByIdReducerCreate = ({ notifyEvent }: Options): Reducer<HandlersByIdState, HandlerEntryAction> =>
  createReducer({} as HandlersByIdState, (builder) => {
    builder.addCase(addHandlerAction, (state, action) => {
      const { id, label, cases } = action.payload;
      state[id] = {
        label,
        cases,
      };
      if (notifyEvent) {
        // Create a shallow copy of cases to avoid passing Immer draft proxy
        const casesSnapshot = cases?.map((c) => ({ ...c }));
        notifyEvent({ type: "HANDLER_ADDED", id, cases: casesSnapshot, label });
      }
    });

    builder.addCase(removeHandlerAction, (state, action) => {
      const { id: handlerId } = action.payload;
      const handler = state[handlerId];
      if (!handler) return;
      if (notifyEvent) {
        const { label } = handler;
        // Create a shallow copy of cases to avoid passing Immer draft proxy
        const casesSnapshot = handler.cases?.map((c) => ({ ...c }));
        notifyEvent({ type: "HANDLER_REMOVED", id: handlerId, cases: casesSnapshot, label });
      }
      delete state[handlerId];
    });

    builder.addCase(updateHandlerAction, (state, action) => {
      const { id: handlerId, label, cases } = action.payload;
      const handler = state[handlerId];
      if (!handler) return;
      if (label !== undefined) {
        handler.label = label;
      }
      if (cases !== undefined) {
        handler.cases = cases;
      }
      if (notifyEvent) {
        // Create a shallow copy of cases to avoid passing Immer draft proxy
        const casesSnapshot = handler.cases?.map((c) => ({ ...c }));
        notifyEvent({ type: "HANDLER_UPDATED", id: handlerId, cases: casesSnapshot, label: handler.label });
      }
    });
  });
